"use client";

import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  Sparkles,
  GraduationCap,
  Users,
  TrendingUp,
  Heart,
  ExternalLink,
  FileText,
  CheckCircle2,
  Briefcase,
  Clock,
  MapPin,
} from "lucide-react";
import { SITE_EMAIL } from "@/lib/site-config";

/** ── Replace with your real Google Form URL ── */
const GOOGLE_FORM_URL = "https://forms.google.com/your-form-id";

const PERKS = [
  {
    icon: TrendingUp,
    title: "Career Growth",
    description:
      "Advance from tutor to senior instructor with structured growth paths.",
  },
  {
    icon: Users,
    title: "Impact Lives",
    description:
      "Help students break into data science and land their dream roles.",
  },
  {
    icon: Heart,
    title: "Supportive Culture",
    description:
      "Join a tight-knit team that celebrates teaching and continuous learning.",
  },
  {
    icon: Clock,
    title: "Flexible Hours",
    description:
      "Evening & weekend batches — ideal for working professionals who love to teach.",
  },
];

const REQUIREMENTS = [
  "Strong proficiency in at least one: Python, SQL, Machine Learning, Tableau, Power BI, SAS, or Advanced Excel",
  "Minimum 5 years of hands-on industry or teaching experience",
  "Excellent communication skills in English and Hindi",
  "Passion for mentoring and helping students achieve career goals",
  "Available for in-person sessions at our Mehrauli, New Delhi center (hybrid options may be available)",
];

/** ✅ Fixed Framer Motion typing */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: [0.25, 0.1, 0.25, 1] as const,
    },
  }),
};

export default function CareersPage() {
  return (
    <div className="min-h-screen flex flex-col overflow-hidden relative">
      <Navbar />

      <main className="flex-1 pt-20 relative z-10">
        {/* ───── Hero ───── */}
        <section className="relative min-h-[60vh] flex items-center justify-center pt-20 pb-16 overflow-hidden bg-background">
          {/* Background effects */}
          <div className="absolute inset-0 bg-grid opacity-50 dark:opacity-20 z-0 mask-image-gradient" />
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-mont-orange/15 dark:bg-mont-orange/25 rounded-full blur-[140px] pointer-events-none z-0" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-mont-violet/10 dark:bg-mont-violet/20 rounded-full blur-[120px] pointer-events-none z-0" />

          <div className="container relative z-10 mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-mont-orange/30 bg-mont-orange/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-mont-orange mb-6">
                <Briefcase className="h-3.5 w-3.5" />
                We&apos;re Hiring
              </div>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold font-heading text-foreground mb-6 leading-tight">
                Teach the Future of{" "}
                <span className="text-gradient">Data Science</span>
              </h1>

              <p className="max-w-2xl mx-auto text-lg md:text-xl text-muted-foreground mb-10 leading-relaxed">
                Join MONT2 Academy as a tutor and inspire the next generation of
                data professionals. Share your expertise, grow your career, and
                make a real impact.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  href={GOOGLE_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="lg"
                    className="h-14 px-8 rounded-2xl bg-gradient-primary text-white font-semibold text-base border-0 hover:opacity-90 shadow-[0_0_30px_rgba(139,92,246,0.3)] transition-all hover:shadow-[0_0_40px_rgba(139,92,246,0.5)] hover:scale-[1.02]"
                  >
                    <FileText className="mr-2 h-5 w-5" />
                    Apply Now — Google Form
                    <ExternalLink className="ml-2 h-4 w-4 opacity-60" />
                  </Button>
                </Link>

                <Link href="#requirements">
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-14 px-8 rounded-2xl font-semibold text-base border-border/80 hover:border-mont-violet/50 hover:bg-mont-violet/5"
                  >
                    View Requirements
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ───── Why Join Us ───── */}
        <section className="relative py-20 bg-muted/25 border-y border-border">
          <div className="container mx-auto px-6 lg:px-12">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="text-center mb-14"
            >
              <motion.div variants={fadeUp} custom={0}>
                <div className="inline-flex items-center gap-2 rounded-full border border-mont-violet/30 bg-mont-violet/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-mont-violet mb-4">
                  <Sparkles className="h-3.5 w-3.5" />
                  Why MONT2
                </div>
              </motion.div>

              <motion.h2
                variants={fadeUp}
                custom={1}
                className="text-3xl md:text-5xl font-bold font-heading text-foreground mb-4"
              >
                Why Teach With{" "}
                <span className="text-gradient">MONT2 Academy?</span>
              </motion.h2>

              <motion.p
                variants={fadeUp}
                custom={2}
                className="max-w-xl mx-auto text-muted-foreground"
              >
                More than a job — it&apos;s a mission. Here&apos;s what makes us
                different.
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {PERKS.map((perk, i) => (
                <motion.div
                  key={perk.title}
                  variants={fadeUp}
                  custom={i}
                  className="group relative rounded-2xl border border-border/80 bg-card/60 p-6 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-mont-violet/40 hover:shadow-lg hover:shadow-mont-violet/5 hover:-translate-y-1"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-mont-violet/20 to-mont-orange/10 text-mont-violet transition-transform duration-300 group-hover:scale-110">
                    <perk.icon className="h-6 w-6" />
                  </div>

                  <h3 className="text-lg font-bold font-heading text-foreground mb-2">
                    {perk.title}
                  </h3>

                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {perk.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ───── Requirements ───── */}
        <section
          id="requirements"
          className="relative py-20 bg-background scroll-mt-28"
        >
          <div className="container mx-auto px-6 lg:px-12">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="max-w-3xl mx-auto"
            >
              <motion.div
                variants={fadeUp}
                custom={0}
                className="text-center mb-12"
              >
                <div className="inline-flex items-center gap-2 rounded-full border border-mont-violet/30 bg-mont-violet/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-mont-violet mb-4">
                  <GraduationCap className="h-3.5 w-3.5" />
                  What We Look For
                </div>

                <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground mb-4">
                  Tutor <span className="text-gradient">Requirements</span>
                </h2>

                <p className="text-muted-foreground">
                  We value passion and practical experience above all else.
                </p>
              </motion.div>

              <div className="space-y-4">
                {REQUIREMENTS.map((req, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    custom={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="flex items-start gap-4 rounded-xl border border-border/60 bg-card/40 p-5 transition-all duration-200 hover:border-mont-violet/30 hover:bg-card/70"
                  >
                    <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-mont-violet" />

                    <p className="text-sm md:text-base text-foreground/90 leading-relaxed">
                      {req}
                    </p>
                  </motion.div>
                ))}
              </div>

              <motion.div
                variants={fadeUp}
                custom={REQUIREMENTS.length}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="mt-6 flex items-start gap-4 rounded-xl border border-mont-orange/30 bg-mont-orange/5 p-5"
              >
                <MapPin className="h-5 w-5 mt-0.5 shrink-0 text-mont-orange" />

                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Location:</strong> First Floor, LIC Building, Near Mehrauli Bus Stand, South Delhi, New Delhi - 110030
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ───── Apply CTA ───── */}
        <section className="relative py-20 bg-muted/25 border-y border-border overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-mont-violet/10 dark:bg-mont-violet/20 rounded-full blur-[120px] pointer-events-none" />

          <div className="container relative z-10 mx-auto px-6 lg:px-12">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="max-w-2xl mx-auto text-center"
            >
              <motion.h2
                variants={fadeUp}
                custom={0}
                className="text-3xl md:text-5xl font-bold font-heading text-foreground mb-4"
              >
                Ready to <span className="text-gradient">Apply?</span>
              </motion.h2>

              <motion.p
                variants={fadeUp}
                custom={1}
                className="text-muted-foreground mb-8 leading-relaxed"
              >
                Fill out the Google Form below with your details and upload your
                resume. Our team will review your application and get back to
                you within a week.
              </motion.p>

              <motion.div
                variants={fadeUp}
                custom={2}
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
              >
                <Link
                  href={GOOGLE_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="lg"
                    className="h-14 px-10 rounded-2xl bg-gradient-primary text-white font-semibold text-base border-0 hover:opacity-90 shadow-[0_0_30px_rgba(139,92,246,0.3)] transition-all hover:shadow-[0_0_40px_rgba(139,92,246,0.5)] hover:scale-[1.02]"
                  >
                    <FileText className="mr-2 h-5 w-5" />
                    Open Application Form
                    <ExternalLink className="ml-2 h-4 w-4 opacity-60" />
                  </Button>
                </Link>
              </motion.div>

              <motion.p
                variants={fadeUp}
                custom={3}
                className="mt-6 text-xs text-muted-foreground"
              >
                Or email your resume directly to{" "}
                <Link
                  href={`mailto:${SITE_EMAIL}?subject=Tutor Application — MONT2 Academy`}
                  className="text-mont-violet hover:underline font-medium"
                >
                  {SITE_EMAIL}
                </Link>
              </motion.p>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}